<template>
    <ElementLayout>
        <template #element>
            <div class="w-100 BrokerWrapper">
                <div class="form-group w-100">
                    <div class="input-group w-100">
                        <input
                            ref="membershipInput"
                            type="search"
                            autocomplete="off"
                            class="form-control"
                            id="membershipId"
                            name="MembershipId"
                            :value="typedMembershipId !== null ? typedMembershipId : (el$.model || '')"
                            @input="onMembershipInput($event, el$)"
                            @keydown.enter.prevent="brokerSearch(el$)"
                        />
                        <div class="input-group-append">
                            <AtlasButton
                                variant="secondary"
                                type="button"
                                :outline="true"
                                id="BrokerSearch"
                                :disabled="loading"
                                @click="brokerSearch(el$)"
                                >Find agent
                                <em
                                    id="search-icon"
                                    class="mL-2 far"
                                    :class="loading ? 'fa-circle-notch fa-spin' : 'fa-search'"
                                ></em></AtlasButton>
                        </div>
                    </div>
                    <div v-if="searchError" class="small text-danger mt-1">{{ searchError }}</div>
                    <div v-if="membershipInfo" class="small text-info mt-1">{{ membershipInfo }}</div>
                </div>
                <div v-if="hasAgentToShow(el$)" class="form-group w-100 BrokerSearchList">
                    <label class="fw-500 d-block" v-if="successfulSearch">Please select an agent</label>
                    <select
                        class="form-control w-100"
                        name="AgentUser"
                        id="AgentUser"
                        :value="el$.form$?.model?.[agentUserPath] ?? ''"
                        @change="onAgentChange($event, el$)"
                    >
                        <option :value="null">Please select</option>
                        <option v-for="a in getAgentOptions(el$)" :key="a.value" :value="a.text">
                            {{ a.text }}
                        </option>
                    </select>
                </div>
            </div>
        </template>

        <template v-for="(component, slot) in elementSlots" #[slot]>
            <slot :name="slot" :el$="el$">
                <component :is="component" :el$="el$" />
            </slot>
        </template>
    </ElementLayout>
</template>

<script>
/**
 * BrokerElement — custom Vueform element for the broker / agent lookup section.
 *
 * Each "Find agent" click runs two independent API calls:
 *   1. POST /Agents/GetAgentsByMembershipId  — populates the agent dropdown.
 *   2. GET  /api/v1/quotes/membership-details — classifies the member as
 *      Aggregator / MoneySuperMarket / Ceta.
 *
 * The save/show-error decision after those calls lives in brokerSearch():
 *   (a) Agents found             → save ID, pre-select first agent.
 *   (b) No agents, valid type    → save ID, clear stale agent, show "You are: …".
 *   (c) No agents, no type       → save ID, clear stale agent, show error.
 *   (d) Membership API failed    → keep existing saved data, show retry message.
 *
 * Because this is a Vueform element, writes go through el$.form$ helpers
 * rather than v-model.
 */

import { defineElement } from '@vueform/vueform'
import { ref, onMounted } from 'vue'
import AtlasButton from "@/components/Common/AtlasButton.vue";
import { useRiskStore } from '@/stores/riskData'

const NO_AGENTS_MESSAGE = 'This member number is not linked to any agents'
const VALIDATE_FAILURE_MESSAGE = 'Unable to validate member number. Please try again.'

// Field paths written by the membership-details lookup. Strings — they must
// match the form schema in consuming tenants (e.g. CetaHousehold/schema.ts).
// Don't rename without updating the consumers.
const FIELD_IS_AGG = 'isAGG'
const FIELD_IS_MSM = 'isMSM'
const FIELD_IS_CETA_MEMBERSHIP_ID = 'isCetaMembershipId'
const FIELD_IS_ON_ACCOUNT = 'isOnAccount'

export default defineElement({
    name: 'BrokerElement',

    components: {
        AtlasButton,
    },

    props: {
        agentUserPath: { type: String, default: 'AgentUser' },
        agentIdPath: { type: String, default: 'AgentId' },
    },

    setup(props, { element }) {
        const riskStore = useRiskStore()

        // Agents returned by the most recent /Agents lookup, transformed
        // into { text, value } pairs for the dropdown.
        const agents = ref([])

        const loading = ref(false)

        //   searchError    — red, for failures and "no agents" messages.
        //   membershipInfo — blue, for "You are: Aggregator User" etc.
        const searchError = ref(null)
        const membershipInfo = ref(null)

        // True only after a fresh, successful agent lookup this session.
        // Drives the "Please select an agent" label visibility.
        const successfulSearch = ref(false)

        const isOnAccount = ref(null)

        const typedMembershipId = ref(null)

        // Template ref to the member-number input.
        const membershipInput = ref(null)

        // Read the currently-saved AgentUser / AgentId straight from the form.
        const savedAgentUser = (el$) => el$?.form$?.model?.[props.agentUserPath] ?? null
        const savedAgentId = (el$) => el$?.form$?.model?.[props.agentIdPath] ?? null

        // Show the agent dropdown if we either just fetched agents or are
        // rehydrating a previously-saved selection.
        const hasAgentToShow = (el$) =>
            agents.value.length > 0 || !!savedAgentUser(el$) || !!savedAgentId(el$)

        // Options for the agent dropdown: fresh agents if we have them,
        // otherwise a single rehydrated option built from saved values.
        const getAgentOptions = (el$) => {
            if (agents.value.length) return agents.value
            const text = savedAgentUser(el$)
            const value = savedAgentId(el$)
            if (text || value) return [{ text: text ?? '', value: value ?? '' }]
            return []
        }

        // Write a value to a Vueform field by path. Silently no-ops if the
        // field isn't registered on the active schema — relied on for the
        // optional isAGG / isMSM / isCetaMembershipId paths which may not exist on
        // every consuming schema.
        const resolveForm = (el$) => el$?.form$?.value ?? el$?.form$

        const updateField = (el$, path, value) => {
            resolveForm(el$)?.el$?.(path)?.update(value ?? null)
        }

        const persistAgentSelection = (el$, agentUser, agentId) => {
            const userValue = agentUser ?? ''
            const idValue = agentId ?? ''

            if (userValue && !idValue) {
                console.warn('BrokerElement: selected agent has no agency id; commission may not apply', userValue)
            }

            updateField(el$, props.agentUserPath, userValue)
            updateField(el$, props.agentIdPath, idValue)

            const form = el$?.form$
            if (typeof form?.updateModel === 'function') {
                form.updateModel(props.agentUserPath, userValue)
                form.updateModel(props.agentIdPath, idValue)
            }
        }

        const onMembershipInput = (event) => {
            typedMembershipId.value = event?.target?.value ?? ''
        }

        // Persist AGG / MSM / isCetaMembershipId to the form model.
        //
        // Writes happen in two places because consuming schemas vary:
        //   - root-level (isAGG, isMSM, isCetaMembershipId) for schemas that bind directly
        //   - AdditionalFormData.* for schemas that nest the same flags
        const updateAdditionalFormDataFlags = (el$, flags) => {
            const form = resolveForm(el$)
            const currentAdditionalFormData = form?.model?.AdditionalFormData ?? {}

            const nextAdditionalFormData = {
                ...currentAdditionalFormData,
                isAGG: flags.isAGG,
                isMSM: flags.isMSM,
                isCetaMembershipId: flags.IsCeta,
            }

            // Direct field writes first — these target the schema's fields
            // and survive even if the bulk model write below is rejected.
            updateField(el$, FIELD_IS_AGG, flags.isAGG)
            updateField(el$, FIELD_IS_MSM, flags.isMSM)
            updateField(el$, FIELD_IS_CETA_MEMBERSHIP_ID, flags.IsCeta)

            if (typeof form?.updateModel === 'function') {
                // Path-targeted writes — preferred because a bulk write can
                // re-apply stale values that other branches just cleared.
                form.updateModel('AdditionalFormData', nextAdditionalFormData)
                form.updateModel(FIELD_IS_AGG, flags.isAGG)
                form.updateModel(FIELD_IS_MSM, flags.isMSM)
                form.updateModel(FIELD_IS_CETA_MEMBERSHIP_ID, flags.IsCeta)
                return
            }

            if (typeof form?.update === 'function') {
                // Fallback for Vueform versions without updateModel().
                form.update({
                    ...(form.model ?? {}),
                    AdditionalFormData: nextAdditionalFormData,
                    [FIELD_IS_AGG]: flags.isAGG,
                    [FIELD_IS_MSM]: flags.isMSM,
                    [FIELD_IS_CETA_MEMBERSHIP_ID]: flags.IsCeta,
                })
            }
        }

        const persistMembershipClassification = (el$, { onAccount, membershipType, paymentType, commission }) => {
            isOnAccount.value = onAccount

            riskStore.setMembershipType(membershipType)

            updateField(el$, FIELD_IS_ON_ACCOUNT, onAccount)

            const form = resolveForm(el$)
            if (typeof form?.updateModel === 'function') {
                const currentAdditionalFormData = form?.model?.AdditionalFormData ?? {}
                form.updateModel('AdditionalFormData', {
                    ...currentAdditionalFormData,
                    isOnAccount: onAccount,
                    membershipType,
                    paymentType,
                    // Commission bounds { minimum, maximum, increment } for the quote page.
                    commission,
                })
            }
        }

        // GET /api/v1/quotes/membership-details — returns
        //   { flags: { isAGG, isMSM, IsCeta }, trueTypes: string[],
        //     commission: { minimum, maximum, default, increment } | null }
        //
        // productId is required by the API: it scopes the commission band to the agency's
        // settings for this product. window.modelData is the only reliable source on the page —
        // the risk store's productId is never populated (see Factory.vue seedMockAddonsPayload).
        const fetchMembershipTypes = async (membershipId) => {
            try {
                const headers = {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${window.loginToken}`,
                }
                // Local check first, matching the other direct-to-API callers (useAgentPolicies,
                // BCISCalculator, Navbar/Search). apiRootUrl is always set by _Layout, so testing
                // it first would make the localhost branch unreachable.
                const baseUrl = location.origin.includes('local')
                    ? 'http://localhost:5008'
                    : (window.apiRootUrl ?? '')

                const query = new URLSearchParams({
                    membershipId,
                    productId: window.modelData?.ProductId ?? '',
                })

                const res = await fetch(
                    `${baseUrl}/api/v1/quotes/membership-details?${query}`,
                    { method: 'GET', headers },
                )

                if (!res.ok) return null

                const data = await res.json()

                // Prefer the new `type` / `paymentType` fields; fall back to the
                // legacy boolean flags when they aren't present so nothing breaks.
                //
                // `type` is the serialised MembershipType enum name, lowercased here:
                // 'moneysupermarket' | 'aggregator' | 'broker' | 'ceta' | 'unknown'.
                // Match on those exact names — not the 'msm'/'agg' membership-id
                // prefixes the API uses internally.
                const membershipType = (data.type ?? '').toString().toLowerCase() || null
                const paymentType = data.paymentType ?? null

                const flags = {
                    isAGG: membershipType ? membershipType === 'aggregator' : !!data.isAggregatorUser,
                    isMSM: membershipType ? membershipType === 'moneysupermarket' : !!data.isMoneySuperMarketUser,
                    IsCeta: membershipType ? membershipType === 'ceta' : !!data.isCeta,
                }

                const isOnAccount = paymentType != null
                    ? paymentType === 'OnAccount'
                    : (typeof data.isOnAccount === 'boolean' ? data.isOnAccount : null)

                // Commission config for this membership: { minimum, maximum, increment,
                // default, policyCommissionRate }. Consumed on the quote page to seed
                // and validate the commission input.
                const commission = data.commission ?? null

                return {
                    flags,
                    membershipType,
                    paymentType,
                    trueTypes: [
                        flags.isAGG && 'Aggregator User',
                        flags.isMSM && 'MoneySuperMarket User',
                        flags.IsCeta && 'Ceta User',
                    ].filter(Boolean),
                    isOnAccount,
                    commission,
                }
            } catch {
                return null
            }
        }

        // Run the membership-details lookup and persist its flags. Returns
        // a small status object the caller uses to pick a save/error strategy:
        //   - membershipChecked: did the API itself return cleanly?
        //   - hasTrueType:       was the member classified as AGG / MSM / Ceta?
        const updateMembershipInfo = async (membershipId, el$) => {
            const membershipDetails = await fetchMembershipTypes(membershipId)
            if (!membershipDetails) {
                return {
                    membershipChecked: false,
                    hasTrueType: false,
                }
            }

            const trueTypes = membershipDetails.trueTypes
            const onAccount = typeof membershipDetails.isOnAccount === 'boolean' ? membershipDetails.isOnAccount : null

            // Updates all flags with new ones
            updateAdditionalFormDataFlags(el$, membershipDetails.flags)

            persistMembershipClassification(el$, {
                onAccount,
                membershipType: membershipDetails.membershipType,
                paymentType: membershipDetails.paymentType,
                commission: membershipDetails.commission,
            })

            if (trueTypes.length) {
                membershipInfo.value = `You are: ${trueTypes.join(', ')}`
            }

            return {
                membershipChecked: true,
                hasTrueType: trueTypes.length > 0,
            }
        }

        const brokerSearch = async (el$) => {
            successfulSearch.value = false
            searchError.value = null
            membershipInfo.value = null
            agents.value = []
            isOnAccount.value = null

            const id = (typedMembershipId.value ?? el$?.model ?? '').toString().trim()
            if (!id) {
                searchError.value = 'Please enter a member number.'
                return
            }

            const token = document.querySelector('input[name="__RequestVerificationToken"]')?.value
            const body = new URLSearchParams({ Id: id })
            const headers = { 'X-Requested-With': 'XMLHttpRequest' }
            if (token) {
                headers['RequestVerificationToken'] = token
                body.set('__RequestVerificationToken', token)
            }

            loading.value = true
            try {
                // 1. Agent lookup -------------------------------------------
                const agentsRes = await fetch(
                    '/Agents/GetAgentsByMembershipId',
                    { method: 'POST', headers, body },
                )
                if (agentsRes.ok) {
                    agents.value = (await agentsRes.json()).map((a) => ({
                        text: `${a.fullName} - ${a.email}`,
                        value: a.attributes?.agentId?.[0] ?? null,
                    }))

                    // Pre-select the first agent so the form has *something*
                    // saved even if the user clicks Next without touching
                    // the dropdown.
                    if (agents.value.length) {
                        persistAgentSelection(el$, agents.value[0].text, agents.value[0].value)

                        successfulSearch.value = true
                    }
                }

                // 2. Membership classification ------------------------------
                // Runs regardless of the agent result, because a number can
                // be classifiable (e.g. AGG user) without any linked agents.
                const membershipResult = await updateMembershipInfo(id, el$)

                // 3. Decide what to save & what to show ---------------------

                // (a) Agents found — commit the ID, keep the pre-selected agent.
                if (successfulSearch.value) {
                    el$?.update(id)
                    typedMembershipId.value = id
                    return
                }

                // (b) No agents but membership is classifiable — commit the
                //     ID, clear the now-stale agent fields, and let the
                //     "You are: …" info line stand on its own.
                if (membershipResult.membershipChecked && membershipResult.hasTrueType) {
                    el$?.update(id)
                    typedMembershipId.value = id

                    // Empty strings, not null — null can be dropped by merge rules.
                    persistAgentSelection(el$, '', '')
                    return
                }

                // (c) No agents AND no recognised type — still commit the
                //     new ID and clear the stale agent, but surface the error.
                if (membershipResult.membershipChecked) {
                    el$?.update(id)
                    typedMembershipId.value = id

                    persistAgentSelection(el$, '', '')

                    searchError.value = NO_AGENTS_MESSAGE
                }

                // (d) Membership API itself failed — keep existing saved
                //     data and ask the user to retry.
                if (!membershipResult.membershipChecked) {
                    searchError.value = VALIDATE_FAILURE_MESSAGE
                }
            } finally {
                loading.value = false
            }

            const hasSavedFallbackAgent = !!savedAgentUser(el$) || !!savedAgentId(el$)
            if (!successfulSearch.value && !membershipInfo.value && !hasSavedFallbackAgent) {
                searchError.value = NO_AGENTS_MESSAGE
            }
        }

        //On mount we check the feature flags as a new quote will have this field prepopulated with a ceta ID.
        onMounted(async () => {
            const id = (membershipInput.value?.value ?? '').trim()
            if (!id) return

            await updateMembershipInfo(id, element)
        })

        // Picking an agent: keep AgentUser (text) and AgentId (value) in sync.
        const onAgentChange = (event, el$) => {
            const text = event.target.value ?? ''
            const match = getAgentOptions(el$).find((a) => a.text === text)
            persistAgentSelection(el$, text, match?.value ?? '')
        }

        return {
            loading,
            searchError,
            membershipInfo,
            agentUserPath: props.agentUserPath,
            agentIdPath: props.agentIdPath,
            hasAgentToShow,
            getAgentOptions,
            typedMembershipId,
            membershipInput,
            onMembershipInput,
            brokerSearch,
            onAgentChange,
            successfulSearch,
            isOnAccount,
        }
    },
})
</script>
